import React from 'react';
import PluginHeader from '../PluginHeader';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";

const PrintStationsPlugin = () => {
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>Print stations - Aronium</title>
                    <meta name="description" content="Print specific products to different kitchen printers and keep kitchen informed of incoming orders" />
                </Helmet>
            </HelmetProvider>

            <section className="py-8 max-w-screen-xl px-4 mx-auto">
                <div className="max-w-screen-xl mx-auto text-center">
                    <h1 className="text-4xl mb-4">
                        Print stations & multiple kitchen printers
                    </h1>
                    <p>
                        Optimize your order management by directing specific products to designated kitchen or order printers.
                    </p>
                    {/* Call to Action Buttons */}
                    <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                        {/* Download for Aronium Lite Button */}
                        <a
                            href={`${BASE_URL}/plugins/print-stations/latest/Aronium.Pos.Print.Stations.Lite.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-[#53759c] hover:bg-[#7589a3] transition duration-300 shadow-lg focus:ring-4 focus:ring-slate-500 focus:outline-none hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Lite
                        </a>

                        {/* Download for Aronium Pro Button */}
                        <a
                            href={`${BASE_URL}/plugins/print-stations/latest/Aronium.Pos.Print.Stations.Pro.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-[#2d4356] hover:bg-[#161a1d] transition duration-300 shadow-lg focus:ring-4 focus:ring-slate-800 focus:outline-none hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Pro
                        </a>
                    </div>
                </div>
            </section>

            <div className="flex flex-col text-center mt-10">
                <div className="grid place-content-center justify-items-center px-4">
                    <img src="/images/print-stations-settings-screen.png" className='rounded-t-lg xl:max-w-6xl' alt="Print stations configuration" />
                </div>
            </div>

            <section className='bg-slate-200 text-gray-700 py-20'>
                <PluginHeader price="5" />
            </section>

            {/* Overview Section */}
            <div>
                <div className="max-w-7xl mx-auto text-center py-24 px-8 lg:px-12">
                    <h2 className="text-5xl text-gray-800 leading-tight mb-8">
                        Why use <span className='font-semibold'>Print stations</span> plugin?
                    </h2>
                    <p className="text-lg text-gray-600 mx-auto">
                        The <span className='font-semibold'>Print stations</span> plugin enhances operational efficiency by allowing businesses to assign different products or product groups to specific printers. This functionality is particularly beneficial for establishments with multiple preparation areas, such as kitchens and bars, ensuring that each station receives only the orders relevant to them.
                    </p>
                </div>
            </div>

            {/* Key Features Section */}
            <div className="pb-24">
                <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                    <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
                        <h3 className="text-2xl font-semibold text-gray-800">
                            Customizable print routing
                        </h3>
                        <p className="mt-4 text-gray-600">
                            Assign products or entire product groups to designated print stations, ensuring orders are sent to the appropriate preparation area.
                        </p>
                    </div>
                    <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
                        <h3 className="text-2xl font-semibold text-gray-800">
                            Scalable configuration
                        </h3>
                        <p className="mt-4 text-gray-600">
                            Add multiple print stations as needed and configure printers for each station to match your establishment's layout and workflow.
                        </p>
                    </div>
                </div>
            </div>

            {/* Call to Action Section */}
            <div className="bg-gradient-to-tr from-[#f5f4f3] to-[#486780] py-24 text-center text-white">
                <div className="mx-auto px-4">
                    <h2 className="text-4xl xl:text-6xl mb-6">
                        Ready to optimize your printing workflow?
                    </h2>
                    <p className="text-xl mb-10">
                        Get started with <span className='font-semibold'>Print stations</span> plugin for organized, accurate, and efficient printing in your business.
                    </p>
                    <a
                        href="https://help.aronium.com/hc/en-us/articles/360021868040-Manage-print-stations"
                        className="button-rounded-white px-12 py-4"
                        target="_blank" rel="noreferrer">
                        Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                        </svg>
                    </a>
                </div>
            </div>
        </>
    );
};

export default PrintStationsPlugin;
